import Button from "core/buttons/button/Button";
import Dropdown from "core/dropdown/Dropdown";
import DropdownLinkItem from "core/dropdown/DropdownLinkItem";
import InputGroup from "core/inputs/InputGroup";
import PageContext from "pages/PageContext";
import PropTypes from "prop-types";
import SearchTag from "core/tags/search-tag/SearchTag";
import SendGroupedEmailModal from "components/modal/SendGroupedEmailModal";
import _ from "lodash";
import { ContentNavigationDetail, pathSegmentsHelper } from "utils/helper";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { OnClickOutside } from "utils/functions";
import { Typography } from "core/typography/Typography";
import { getProperSize, phoneSize, smallDesktopSize } from "utils/screenSizes";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  ALL_SEARCH_TAG,
  BILLING_COMPANY_SEARCH_TAG,
  CLIENT_SEARCH_TAG,
  CONSULTANTS,
  CONTACT_SEARCH_TAG,
  STAFF_SEARCH_TAG,
  STR_COMPANY_SEARCH_TAG,
  PARTNER_STR_COMPANY_SEARCH_TAG,
  SUBCONTRACTOR_SEARCH_TAG,
} from "constants/header";
import { canUserPerformAction } from "access-roles/general-functions";

import Icon from "core/icon/Icon";
import { useUserContext } from "api/user/UserProvider";
import {
  BILLING_CLIENT_ROLES,
  CLIENT_ROLES,
  CONSULTANT_ROLES,
  MISSION_ROLES,
  ROLE_GROUPED_MAIL,
  STAFF_ROLES,
  SUBCONTRACTOR_COMPANY_ROLES,
  SUBCONTRACTOR_ROLES,
} from "access-roles/roles";
import CategoryTag from "core/tags/category-tag/CategoryTag";
import { TAG_TYPES } from "constants/constants";

const BottomHeader = ({ navigate }) => {
  const [headerContent, setHeaderContent] = useState({});
  const [isOpen, setIsOpen] = useState({ mail: false, add: false });
  const [listening, setListening] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [searchTags, setSearchTags] = useState([]);
  const [isOpenDropdownSearch, setIsOpenDropdownSearch] = useState(false);
  const [isMailingListModalOpened, setIsMailingListModalOpened] =
    useState(false);

  const [groupEmailEntities, setGroupEmailEntities] = useState([]);
  const [isButtonsDisplay, setIsButtonsDisplay] = useState(true);
  const [selectedItemIndex, setSelectedItemIndex] = useState(-1);
  const routerNavigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const dropdownSearchRef = useRef(null);
  const resultListRef = useRef(null);
  const intl = useIntl();

  const {
    search,
    searchResult,
    user,
    userScope,
    setSlugSearched,
    hierarchyEmailAddresses,
    getHierarchyEmailAddresses,
  } = useContext(PageContext);

  const { userRoleList } = useUserContext();
  const mailListLimit = 50;

  useEffect(() => {
    const classicMailtoAction = (mailingList) =>
      (window.location.href = `mailto:${mailingList}`);
    if (hierarchyEmailAddresses?.count) {
      hierarchyEmailAddresses?.count <= mailListLimit
        ? classicMailtoAction(hierarchyEmailAddresses?.emails)
        : setIsMailingListModalOpened(true);
    }
  }, [hierarchyEmailAddresses]);

  useEffect(() => {
    if (!_.isEmpty(userRoleList)) {
      const list = _.chain([])
        // TODO HF-0250 : filtre Contact visible pour tous (pas encore de rôle ni
        // d'entité Contact côté backend ; renvoie 0 résultat sans erreur).
        .concat(CONTACT_SEARCH_TAG)
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles: STAFF_ROLES.ROLE_GET_STAFF,
          })
            ? STAFF_SEARCH_TAG
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles:
              SUBCONTRACTOR_COMPANY_ROLES.ROLE_GET_SUBCONTRACTOR_COMPANY,
          })
            ? STR_COMPANY_SEARCH_TAG
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles:
              SUBCONTRACTOR_COMPANY_ROLES.ROLE_GET_SUBCONTRACTOR_COMPANY,
          })
            ? PARTNER_STR_COMPANY_SEARCH_TAG
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles: CLIENT_ROLES.ROLE_GET_CLIENT,
          })
            ? CLIENT_SEARCH_TAG
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles: CONSULTANT_ROLES.ROLE_GET_CONSULTANT,
          })
            ? CONSULTANTS
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles: BILLING_CLIENT_ROLES.ROLE_GET_BILLING_CLIENT,
          })
            ? BILLING_COMPANY_SEARCH_TAG
            : null
        )
        .concat(
          canUserPerformAction({
            userRoles: userRoleList,
            requiredRoles: SUBCONTRACTOR_ROLES.ROLE_GET_SUBCONTRACTOR,
          })
            ? SUBCONTRACTOR_SEARCH_TAG
            : null
        )
        .compact()
        .uniqWith(_.isEqual)
        .sortBy((tag) => tag.defaultMessage)
        .value();
      const allFilters = _.chain(list).map("value").uniq().join(",").value();
      setSearchTags([{ ...ALL_SEARCH_TAG, value: allFilters }, ...list]);
    }
  }, [user, userRoleList]);

  const performSearch = useCallback(() => {
    const activeTags = searchTags.filter((elm) => elm.active);

    const isPartnerConnection = activeTags.some((e) => e?.isPartnerConnection);

    if (activeTags.length === 1 && activeTags[0]?.translationId === "all")
      search({ search: searchField, profile: activeTags[0]?.value });
    else {
      const profiles = activeTags
        .filter((elm) => elm.active)
        .map((elm) => elm.value)
        .join(",");

      search({
        search: searchField,
        profile: profiles,
        isPartnerConnection,
      });
    }
  }, [search, searchField, searchTags]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  useEffect(() => {
    const resultList = resultListRef.current;
    const resultListItems = resultList?.getElementsByTagName("a");
    const maxSelectedItemIndex = resultListItems?.length - 1;

    const handleKeyDown = (event) => {
      if (!isOpenDropdownSearch || !resultList) return;

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          setSelectedItemIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "ArrowDown":
          event.preventDefault();
          setSelectedItemIndex((prev) =>
            prev < maxSelectedItemIndex ? prev + 1 : prev
          );
          break;
        default:
          break;
      }
    };

    if (resultList && isOpenDropdownSearch) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [searchResult, isOpenDropdownSearch, selectedItemIndex]);

  useEffect(() => {
    const resultList = resultListRef.current;
    const resultListItems = resultList?.getElementsByTagName("a");

    if (resultListItems) {
      for (const element of resultListItems) {
        element.classList.remove("selected-result");
      }
      resultListItems[selectedItemIndex]?.classList.add("selected-result");
    }
  }, [selectedItemIndex]);

  useEffect(() => {
    if (!isOpenDropdownSearch) {
      setSelectedItemIndex(-1);
    }
  }, [isOpenDropdownSearch]);

  useEffect(
    OnClickOutside(listening, setListening, menuRef, () =>
      setIsOpen({ mail: false, add: false })
    )
  );

  useEffect(
    OnClickOutside(listening, setListening, dropdownSearchRef, () => {
      setIsOpenDropdownSearch(false);
    })
  );

  const pathname = location.pathname;
  useEffect(() => {
    const contentFounded = ContentNavigationDetail.find(({ path, params }) => {
      if (!params?.length) return path.includes(pathname);

      const pathSegments = pathSegmentsHelper({ pathname });
      const baseUrl = `/${pathSegments
        .slice(0, pathSegments.length - params.length)
        .join("/")}`;
      return path.includes(pathname) || path.includes(baseUrl);
    });

    contentFounded && setHeaderContent(contentFounded);
  }, [location]);

  const activateSearchTag = (id) => {
    if (id === "all") {
      // When clicking "all", deactivate all other tags and activate only "all"
      setSearchTags([
        ...searchTags.map((el) => {
          return {
            ...el,
            active: el.translationId === "all",
          };
        }),
      ]);
      return;
    }

    const targetIndex = searchTags.findIndex((e) => e.translationId === id);
    if (targetIndex >= 1) {
      searchTags[0].active = false;
      searchTags[targetIndex].active = !searchTags[targetIndex].active;

      const hasActiveTag = searchTags
        .filter((e) => e.translationId !== "all")
        .some((e) => e.active);

      if (!hasActiveTag) {
        searchTags[0].active = true;
      }

      setSearchTags([...searchTags]);
    }

    if (
      searchTags.filter((e) => e.translationId !== "all").every((e) => e.active)
    ) {
      setSearchTags([
        ...searchTags.map((el, i) => {
          return {
            ...el,
            active: i === 0,
          };
        }),
      ]);
    }
  };

  const dropdownSearchField = (e) => {
    setSearchField(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e) {
      e.stopPropagation();
    }
    if (searchField.length >= 3) {
      setIsOpenDropdownSearch(true);
      performSearch();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      const selectedResult = document.querySelector(".selected-result");
      if (selectedResult) {
        e.preventDefault();
        setSearchField("");
        routerNavigate(selectedResult.getAttribute("href"));
      } else {
        handleSearchSubmit();
      }
    }
  };

  useEffect(() => {
    if (searchField.length >= 3) {
      setIsOpenDropdownSearch(true);
    }
  }, [searchField.length]);

  const enableAddButton = () => {
    const requiredRoles = [
      STAFF_ROLES.ROLE_CREATE_STAFF,
      CONSULTANT_ROLES.ROLE_CREATE_CONSULTANT,
      SUBCONTRACTOR_ROLES.ROLE_CREATE_SUBCONTRACTOR,
      SUBCONTRACTOR_COMPANY_ROLES.ROLE_CREATE_SUBCONTRACTOR_COMPANY,
      CLIENT_ROLES.ROLE_CREATE_CLIENT,
      BILLING_CLIENT_ROLES.ROLE_CREATE_BILLING_CLIENT,
      MISSION_ROLES.ROLE_CREATE_MISSION,
    ];
    return requiredRoles.some((role) => userRoleList.includes(role));
  };

  const buttonsDisplay = () => {
    setIsButtonsDisplay(!isButtonsDisplay);
  };

  const renderDropdown = (isOpen, dropdownId, items, titleId) => {
    return (
      <div className={`dropdown ${isOpen ? "active" : "inactive"}`}>
        <Dropdown
          id={dropdownId}
          width={"max-content"}
          marginTop={getProperSize("190", "190", "25", "25", "25", "25")}
          marginLeft={
            dropdownId === "creation-dropdown"
              ? getProperSize("", "", "-130", "-150", "-120", "-130")
              : getProperSize("", "", "-30", "-30", "-30", "-30")
          }
        >
          {phoneSize && (
            <div className="title-4 deep-purple dropdown-link-item">
              <FormattedMessage id={titleId} defaultMessage={titleId} />
            </div>
          )}
          {items.map(({ id, labelId, to, onClick }) => {
            return (
              <DropdownLinkItem key={id} id={id} to={to} onClick={onClick}>
                <FormattedMessage id={labelId} defaultMessage={labelId} />
              </DropdownLinkItem>
            );
          })}
        </Dropdown>
      </div>
    );
  };

  const handleSendGroupEmailBtn = (entities) => {
    getHierarchyEmailAddresses({ entities });
    setGroupEmailEntities(entities);
  };

  const sendEmailGroupDropdownContent = () =>
    renderDropdown(
      isOpen.mail,
      "send-email-dropdown",
      [
        {
          id: "mail_group_cslt",
          labelId: "mail_group_cslt",
          onClick: () => handleSendGroupEmailBtn(["consultant"]),
        },
        {
          id: "mail_group_str",
          labelId: "mail_group_str",
          onClick: () => handleSendGroupEmailBtn(["subcontractor"]),
        },
        {
          id: "mail_group_all",
          labelId: "mail_group_all",
          onClick: () =>
            handleSendGroupEmailBtn(["consultant", "subcontractor"]),
        },
      ],
      "send_mail_group"
    );

  const addDropdownContent = () => {
    const dropdownItems = [
      {
        id: "add_mission",
        labelId: "add_mission",
        to: `/${userScope}/missions/creation`,
        onClick: () => navigate("/missions/creation"),
        roles: MISSION_ROLES.ROLE_CREATE_MISSION,
      },
      {
        id: "add_cslt_member",
        labelId: "add_cslt_member",
        to: `/${userScope}/consultant/creation`,
        onClick: () => navigate("/consultant/creation"),
        roles: CONSULTANT_ROLES.ROLE_CREATE_CONSULTANT,
      },
      {
        id: "add_str_member",
        labelId: "add_str_member",
        to: `/${userScope}/subcontractor/creation`,
        onClick: () => navigate("/subcontractor/creation"),
        roles: SUBCONTRACTOR_ROLES.ROLE_CREATE_SUBCONTRACTOR,
      },
      {
        id: "add_str_company",
        labelId: "add_str_company",
        to: `/${userScope}/subcontractor-company/creation`,
        onClick: () => navigate("/subcontractor-company/creation"),
        roles: SUBCONTRACTOR_COMPANY_ROLES.ROLE_CREATE_SUBCONTRACTOR_COMPANY,
      },
      {
        id: "add_client",
        labelId: "add_client",
        to: `/${userScope}/client/creation`,
        onClick: () => navigate("/client/creation"),
        roles: CLIENT_ROLES.ROLE_CREATE_CLIENT,
      },
      {
        id: "add_billing_client",
        labelId: "add_billing_client",
        to: `/${userScope}/billing-client/creation`,
        onClick: () => navigate("/billing-client/creation"),
        roles: BILLING_CLIENT_ROLES.ROLE_CREATE_BILLING_CLIENT,
      },

      {
        id: "add_staff_member",
        labelId: "add_staff_member",
        to: `/${userScope}/staff/creation`,
        onClick: () => navigate("/staff/creation"),
        roles: STAFF_ROLES.ROLE_CREATE_STAFF,
      },
    ];

    const filteredItems = dropdownItems.filter(({ roles }) =>
      canUserPerformAction({ userRoles: userRoleList, requiredRoles: roles })
    );

    return renderDropdown(
      isOpen.add,
      "creation-dropdown",
      filteredItems,
      "add"
    );
  };

  const getTagTypeFromSearchUserType = (search_user_type) => {
    switch (search_user_type) {
      case "staff":
        return TAG_TYPES.PEOPLE;
      case "consultant":
        return TAG_TYPES.CSLT;
      case "subcontractor":
        return TAG_TYPES.STR;
      case "client":
        return TAG_TYPES.CLIENTS;
      case "prospection":
        return TAG_TYPES.PROSPECTIONS;
      case "subcontractor_company":
        return TAG_TYPES.BRANCHES;
      case "billing_client":
        return TAG_TYPES.BILLING_CLIENT;
      case "contact":
        return TAG_TYPES.CONTACT;
    }
  };

  const renderTagResult = (search_element) => {
    return (
      <CategoryTag
        id={search_element.id}
        label={intl.formatMessage({
          id: search_element.user_type,
          defaultMessage: search_element.user_type,
        })}
        readOnly
        type={getTagTypeFromSearchUserType(search_element.user_type)}
      />
    );
  };

  return (
    <>
      {isMailingListModalOpened && (
        <SendGroupedEmailModal
          onClose={() => {
            setIsMailingListModalOpened(false);
          }}
          groupEmailEntities={groupEmailEntities}
          hierarchyEmailAddresses={hierarchyEmailAddresses}
        />
      )}

      <div className="bottom-container">
        <div className="content">
          <div className="title-container">
            <div>
              {headerContent?.translationId && (
                <FormattedMessage
                  id={headerContent.translationId}
                  defaultMessage={headerContent.defaultMessage}
                />
              )}
            </div>
            <Icon
              icon={isButtonsDisplay ? "arrow-up-2" : "arrow-down-2"}
              color={"white"}
              onClick={buttonsDisplay}
            />
          </div>
          {isButtonsDisplay && (
            <div className="buttons-container">
              <div className="search-parent">
                <div className="search">
                  <InputGroup
                    icon="search"
                    placeholder={intl.formatMessage({
                      id: "search_in_the_directory",
                      defaultMessage: "Rechercher dans l'annuaire",
                    })}
                    onChange={(event) => dropdownSearchField(event)}
                    onKeyDown={handleKeyDown}
                    color="gray-200"
                    value={searchField}
                    iconOnClick={handleSearchSubmit}
                  />
                </div>
                <div
                  className={`dropdown-search ${
                    searchField.length >= 3 && isOpenDropdownSearch
                      ? "active"
                      : ""
                  }`}
                >
                  <Dropdown
                    id="search-user-dropdown"
                    width={phoneSize ? "unset" : smallDesktopSize ? 500 : 690}
                  >
                    <div className="search-filter" ref={dropdownSearchRef}>
                      {searchTags.map((tag) => {
                        return (
                          <SearchTag
                            value={
                              <FormattedMessage
                                id={tag.translationId}
                                defaultMessage={tag.defaultMessage}
                              />
                            }
                            active={tag.active}
                            disabled={false}
                            onClick={() => activateSearchTag(tag.translationId)}
                            key={tag.translationId}
                          />
                        );
                      })}
                    </div>
                    <div className="search-separator" />
                    <div className="search-result">
                      {searchResult?.length === 0 ? (
                        <FormattedMessage
                          id="no_search_result"
                          defaultMessage="Aucun résultat"
                        />
                      ) : (
                        <div className="search-result-list" ref={resultListRef}>
                          {searchResult?.map((elm) => (
                            <Link
                              to={`/${userScope}/${_.replace(
                                elm.user_type,
                                "_",
                                "-"
                              )}/${elm.slug}`}
                              onClick={() => {
                                setSlugSearched(elm?.slug);
                                setSearchField("");
                              }}
                              id={elm.id}
                              key={elm.id}
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                justifyItems: "center",
                              }}
                            >
                              <Typography
                                className="text-2 violet"
                                type="h2"
                                label={`${elm.name}`}
                                style={{
                                  marginRight: "2%",
                                }}
                              />
                              <div
                                style={{
                                  marginTop: "5px",
                                }}
                              >
                                {renderTagResult(elm)}
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </Dropdown>
                </div>
              </div>
              <div ref={menuRef} className="btn-group-bottom">
                {canUserPerformAction({
                  userRoles: userRoleList,
                  requiredRoles: ROLE_GROUPED_MAIL,
                }) && (
                  <Button
                    className={`btn btn-nav btn-nav-outline ${
                      isOpen.mail && "active"
                    }`}
                    color="white"
                    icon={isOpen.mail ? "arrow-up" : "arrow-down"}
                    onClick={() =>
                      setIsOpen({ add: false, mail: !isOpen.mail })
                    }
                  >
                    <FormattedMessage
                      id="send_mail_group"
                      defaultMessage="Envoyer mail groupé"
                    />
                    {!phoneSize && sendEmailGroupDropdownContent()}
                  </Button>
                )}
                {enableAddButton() && (
                  <Button
                    className={`btn-nav btn-nav-outline
                  ${isOpen.add && "active"}`}
                    color="white"
                    icon={isOpen.add ? "arrow-up" : "arrow-down"}
                    onClick={() => setIsOpen({ add: !isOpen.add, mail: false })}
                  >
                    <FormattedMessage id="add" defaultMessage="Ajouter" />
                    {!phoneSize && addDropdownContent()}
                  </Button>
                )}
              </div>
            </div>
          )}
          {isOpen.mail && phoneSize && sendEmailGroupDropdownContent()}
          {isOpen.add && phoneSize && addDropdownContent()}
        </div>
      </div>
    </>
  );
};
BottomHeader.propTypes = {
  navigate: PropTypes.func.isRequired,
};

export default BottomHeader;
